import React from 'react'
import findIndex from 'lodash/findIndex'

import { ApolloError, useMutation } from '@apollo/client'
import AutocompleteSelect from '@/gf/components/AutocompleteSelect'
import UserContext from '@/gf/modules/User'
import LineItemM from '@/gf/modules/LineItem'
import MoneyM from '@/gf/modules/Money'

import useSession from '../../hooks/useSession'
import useMsgs from '@/gf/hooks/useMsgs'
import { UNKNOWN_ERROR } from '@/gf/providers/MsgsProvider'
import useToggle from '@/gf/hooks/useToggle'
import ADD_CUSTOM_PRODUCTS from '../../hooks/useAddCustomProducts'

import TextInput from '@/gf/components/TextInput'
import Card from '@/gf/components/Card'
import ClassificationsSelect from '@/gf/components/ClassificationSelect'
import { Tbody, Td, Th, Thead, Tr } from '@/gf/components/Table'
import PriceInput from '@/gf/components/inputs/PriceV2'
import QuantityInput from '@/gf/components/inputs/Quantity'

import { StoreOption, StoreLookup, EditLineItemChanges } from '../../../types'

import type { PartRequestPlaceholder } from './Details'
import type { Money } from '@/types'

const PartRequestsAddCard = ({
  organizationId,
  setPartRequests,
  allStores,
  additions,
  partRequests,
  onComplete,
  setAdditions,
  initialStore = { value: '', label: '' },
}: {
  organizationId: string | undefined
  setPartRequests: (pr) => void
  allStores: StoreLookup[]
  additions: EditLineItemChanges
  partRequests: PartRequestPlaceholder[]
  onComplete: () => void
  setAdditions: React.Dispatch<React.SetStateAction<EditLineItemChanges>>
  initialStore?: { value: string; label: string }
}) => {
  const [selectedStore, setSelectedStore] = React.useState<StoreOption>(initialStore)
  const { user } = useSession()
  const [spinnerLive, spinner] = useToggle()
  const [_msgs, msgsMgr] = useMsgs()
  const [addProducts] = useMutation(ADD_CUSTOM_PRODUCTS)

  const addCustomProducts = () => {
    if (partRequests.some((pr) => pr.salePrice === undefined)) {
      msgsMgr.add('Please set a price for each product', 'negative')
      return
    }

    if (partRequests.some((pr) => pr.partNumber === undefined || pr.partNumber === '')) {
      msgsMgr.add('Please set a mpn for each product', 'negative')
      return
    }

    if (partRequests.some((pr) => pr.name === undefined || pr.name === '')) {
      msgsMgr.add('Please set a name for each product', 'negative')
      return
    }

    if (allStores.length > 0 && !selectedStore?.value) {
      msgsMgr.add('Select a gearflow user', 'negative')
      return
    }

    spinner.on()

    const variables = {
      organizationId,
      storeId: selectedStore?.value,
      partRequests: partRequests.map((pr) => ({
        inStock: pr.inStock ?? true,
        availableAt: pr.availableAt ?? null,
        name: pr.name,
        mpn: pr.partNumber,
        quantity: pr.count,
        externalId: pr.externalId,
        taskNumber: pr.taskNumber,
        suggestion: pr.suggestion,
        salePrice: pr.salePrice?.toString(),
        classification: pr.classification,
      })),
    }

    addProducts({ variables })
      .then(({ data }) => {
        const allAdditions = data?.addCustomProducts.reduce(
          (acc, lineItem) => ({
            ...acc,
            [lineItem.product.id]: LineItemM.fromPayload(lineItem),
          }),
          additions
        )

        setAdditions(allAdditions)

        msgsMgr.add('Products ready to quote!', 'positive')

        return onComplete()
      })
      .catch((err: ApolloError) => {
        msgsMgr.add(err.message || UNKNOWN_ERROR, 'negative')
      })
      .finally(spinner.off)
  }

  const storeOptions = allStores.map((sto) => ({
    value: sto.id,
    label: `${sto.name}${sto?.alias ? ` (${sto.alias})` : ''}`,
  }))

  const setPlaceholderCount = (value, partRequest) => {
    const placeholders = partRequests.reduce(
      (acc: PartRequestPlaceholder[], pr: PartRequestPlaceholder) => {
        if (pr.id === partRequest.id) {
          const newPlaceholder = {
            ...pr,
            count: parseInt(value, 10),
          }
          return [...acc, newPlaceholder]
        }

        return [...acc, pr]
      },
      []
    )

    setPartRequests(placeholders)
  }

  const setPlaceholderName = (value, partRequest) => {
    const placeholders = partRequests.reduce(
      (acc: PartRequestPlaceholder[], pr: PartRequestPlaceholder) => {
        if (pr.id === partRequest.id) {
          const newPlaceholder = {
            ...pr,
            name: value,
          }
          return [...acc, newPlaceholder]
        }

        return [...acc, pr]
      },
      []
    )

    setPartRequests(placeholders)
  }

  const setPlaceholderPartNumber = (value, partRequest) => {
    const placeholders = partRequests.reduce(
      (acc: PartRequestPlaceholder[], pr: PartRequestPlaceholder) => {
        if (pr.id === partRequest.id) {
          const newPlaceholder = {
            ...pr,
            partNumber: value,
          }
          return [...acc, newPlaceholder]
        }

        return [...acc, pr]
      },
      []
    )

    setPartRequests(placeholders)
  }

  const setPlaceholderPrice = (salePrice: Money | undefined, partRequestId: string) => {
    const index = findIndex(partRequests, (pr) => pr.id === partRequestId)
    const salePriceString = salePrice && MoneyM.toString(salePrice)

    const newPartRequests = Object.assign([], partRequests, {
      [index]: { ...partRequests[index], salePrice: salePriceString },
    })

    setPartRequests(newPartRequests)
  }

  const setPlaceholderClassification = (option, partRequest) => {
    const placeholders = partRequests.reduce(
      (acc: PartRequestPlaceholder[], pr: PartRequestPlaceholder) => {
        if (pr.id === partRequest.id) {
          const classification = parseInt(option.value, 10)
          const newPlaceholder = {
            ...pr,
            classification,
          }
          return [...acc, newPlaceholder]
        }

        return [...acc, pr]
      },
      []
    )

    setPartRequests(placeholders)
  }

  return (
    <div>
      <Card
        title="Add To Quote"
        primaryFooterAction={{
          id: 1,
          title: 'Add Items',
          disabled: spinnerLive,
          showSpinner: spinnerLive,
          callBack: addCustomProducts,
        }}
        secondaryFooterActions={[
          {
            id: 2,
            title: 'Close',
            callBack: onComplete,
          },
        ]}
      >
        <div className="p-6">
          {user && UserContext.isAdmin(user) && (
            <AutocompleteSelect
              label=""
              name="storeSelection"
              className="w-full text-sm mb-1"
              value={allStores.find((s) => s.id === selectedStore.value)}
              options={storeOptions}
              onChange={setSelectedStore}
              reactSelectOptions={{
                isSearchable: true,
                isMulti: false,
                placeholder: 'Select store...',
                defaultValue: selectedStore,
              }}
            />
          )}

          <div className="min-w-full rounded border border-gray-300">
            <div>
              <table className="divide-y divide-gray-200 bg-white min-w-full">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Part Number</Th>
                    <Th style={{ width: '1.25rem' }}>Quantity</Th>
                    <Th>Price</Th>
                    {partRequests.some((pr) => pr.inStock !== undefined) && <Th>Availability</Th>}
                    {user && UserContext.isAdmin(user) && <Th>Classification</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {partRequests.map((partRequest) => (
                    <Tr key={partRequest.id}>
                      <Td className="px-2">
                        <TextInput
                          id="name"
                          placeholder="Name"
                          value={partRequest.name}
                          onChange={(e) => setPlaceholderName(e.target.value, partRequest)}
                        />
                      </Td>
                      <Td className="px-2">
                        <TextInput
                          id="mpn"
                          placeholder="MPN"
                          value={partRequest.partNumber}
                          onChange={(e) => setPlaceholderPartNumber(e.target.value, partRequest)}
                        />

                        {/* <div className="whitespace-normal px-2 py-1 bg-gray-100 border border-gray-300 rounded-md">
                          Requested part:{' '}
                          <span className="font-medium">{partRequest.rfqPartNumber}</span>.
                        </div> */}
                      </Td>
                      <Td className="px-2" style={{ width: '1.25rem' }}>
                        <QuantityInput
                          id="quantity"
                          min={1}
                          value={partRequest.count}
                          onChange={(e) => setPlaceholderCount(e.target.value, partRequest)}
                        />
                      </Td>
                      <Td className="px-2">
                        <PriceInput
                          price={
                            partRequest.salePrice !== undefined
                              ? MoneyM.fromDecimal(partRequest.salePrice, 'USD')
                              : null
                          }
                          onChange={(salePrice) => setPlaceholderPrice(salePrice, partRequest.id)}
                          required
                          min={0.01}
                        />
                      </Td>
                      {partRequest.inStock !== undefined && (
                        <Td className="px-2 text-sm">
                          {partRequest.inStock === true && (
                            <p className="font-semibold">In Stock</p>
                          )}
                          {partRequest.inStock === false && (
                            <div>
                              <p className="font-semibold text-red-500">Backordered</p>
                              {partRequest.availableAt && (
                                <p className="text-sm">
                                  Available {partRequest.availableAt?.toLocaleString()}
                                </p>
                              )}
                            </div>
                          )}
                          {partRequest.inStock === undefined && <span>-</span>}
                        </Td>
                      )}
                      {user && UserContext.isAdmin(user) && (
                        <Td className="px-2">
                          <ClassificationsSelect
                            value={partRequest.classification ?? null}
                            onChange={(classification) =>
                              setPlaceholderClassification(classification, partRequest)
                            }
                          />
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </table>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default PartRequestsAddCard
